import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, QrCode, RefreshCw, Unplug, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { WA_GREEN, formatPhone } from "@/utils/whatsapp";
import { WaDialog } from "./WaDialog";
import { emitWaChanged, type WaStatus } from "./useUserWhatsApp";

type QrResp = WaStatus & { qr_data_url: string | null; pairing_code?: string; expires_at?: string };

function MockScan({ onDone }: { onDone: (s: WaStatus) => void }) {
    const { t } = useTranslation();
    const [phone, setPhone] = useState("+994 ");
    const [busy, setBusy] = useState(false);
    const go = async () => {
        setBusy(true);
        try { const r = await api.post("/user-whatsapp/mock/confirm", { phone }); onDone(r.data); }
        catch (e: any) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="rounded-xl border border-dashed p-3 space-y-2" style={{ borderColor: "var(--warning-fg)", background: "var(--warning-bg)" }} data-testid="wa-qr-mock-panel">
            <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--warning-fg)" }}><FlaskConical size={14} />{t("uwa.mock.title")}</div>
            <div className="text-xs" style={{ color: "var(--warning-fg)" }}>{t("uwa.mock.body")}</div>
            <div className="flex gap-2">
                <input data-testid="wa-qr-mock-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="surface-input px-3 py-2 text-sm flex-1 min-w-0" style={{ minHeight: 38 }} />
                <button type="button" data-testid="wa-qr-mock-confirm" disabled={busy} onClick={go} className="rounded-md px-3 text-xs font-medium text-white disabled:opacity-60" style={{ background: WA_GREEN, minHeight: 38 }}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : t("uwa.mock.simulate")}
                </button>
            </div>
        </div>
    );
}

export function WhatsAppQRModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState<QrResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [left, setLeft] = useState(0);
    const [failed, setFailed] = useState<string | null>(null);
    const [waitStart, setWaitStart] = useState<number | null>(null);
    const [slow, setSlow] = useState(false);

    useEffect(() => {
        if (!loading) { setSlow(false); return; }
        const id = setTimeout(() => setSlow(true), 5000);
        return () => clearTimeout(id);
    }, [loading]);

    const load = useCallback(async (silent = false) => {
        if (!silent) { setLoading(true); setFailed(null); }
        try {
            const r = await api.get("/user-whatsapp/qr", { timeout: 90000 });
            setData(r.data); emitWaChanged(r.data); setFailed(null);
            setWaitStart((w) => (r.data.connected || r.data.qr_data_url ? null : w ?? Date.now()));
        } catch (e: any) {
            const err = e?.response?.data?.detail?.error;
            setFailed(err ? t(`uwa.err.${err}` as any, { defaultValue: formatApiError(e) }) : formatApiError(e));
        } finally { setLoading(false); }
    }, [t]);

    useEffect(() => { if (open) { setWaitStart(null); load(); } else { setData(null); setFailed(null); } }, [open, load]);
    const stalled = !!waitStart && Date.now() - waitStart > 25000;

    useEffect(() => {
        if (!open || !data || data.connected) return;
        const remaining = () => data.expires_at ? Math.max(0, Math.round((new Date(data.expires_at).getTime() - Date.now()) / 1000)) : 0;
        setLeft(remaining());
        const tick = setInterval(() => {
            const s = remaining();
            setLeft(s);
            if (s <= 0) load(true);
        }, 1000);
        const poll = setInterval(async () => {
            try { const r = await api.get("/user-whatsapp/status"); if (r.data.connected) { setData({ ...r.data, qr_data_url: null }); emitWaChanged(r.data); } } catch { /* retry next tick */ }
        }, 3000);
        return () => { clearInterval(tick); clearInterval(poll); };
    }, [open, data, load]);

    const connected = (s: WaStatus) => { setData({ ...s, qr_data_url: null }); emitWaChanged(s); toast.success(t("uwa.qr.connected_toast")); };

    const disconnect = async () => {
        setBusy(true);
        try { const r = await api.post("/user-whatsapp/disconnect"); emitWaChanged(r.data); toast.success(t("uwa.qr.disconnected_toast")); await load(); }
        catch (e: any) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <WaDialog open={open} onClose={onClose} testid="wa-qr-modal" icon={<QrCode size={20} />}
            title={t("uwa.qr.title")} subtitle={t("uwa.qr.subtitle")}>
            {failed && !data ? (
                <div className="space-y-4 py-4 text-center" data-testid="wa-qr-failed">
                    <div className="rounded-xl p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{failed}</div>
                    <button type="button" data-testid="wa-qr-retry" disabled={loading} onClick={() => load()} className="inline-flex items-center justify-center gap-2 rounded-md px-4 text-sm font-medium text-white" style={{ background: WA_GREEN, minHeight: 42 }}>
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />{t("uwa.qr.retry")}
                    </button>
                </div>
            ) : !data ? (
                <div className="flex flex-col items-center gap-2 py-12 text-xs text-center" style={{ color: "var(--text-muted)" }} data-testid="wa-qr-loading"><Loader2 className="animate-spin" size={24} style={{ color: WA_GREEN }} />{slow ? <span data-testid="wa-qr-waking">{t("uwa.qr.waking")}</span> : t("uwa.qr.generating")}</div>
            ) : data.connected ? (
                <div className="space-y-4" data-testid="wa-qr-connected">
                    <div className="flex items-center gap-3 rounded-xl p-4" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}>
                        <CheckCircle2 size={28} />
                        <div>
                            <div className="text-sm font-semibold">{t("uwa.status.connected")}</div>
                            <div className="text-sm tabular-nums" data-testid="wa-qr-connected-phone">{data.phone ? formatPhone(data.phone) : ""}</div>
                            {data.connected_at && <div className="text-xs opacity-80">{t("uwa.qr.since", { date: new Date(data.connected_at).toLocaleString(i18n.language) })}</div>}
                        </div>
                    </div>
                    {data.mocked && <div className="text-xs" style={{ color: "var(--warning-fg)" }} data-testid="wa-qr-mock-note">{t("uwa.mock.note")}</div>}
                    <button type="button" data-testid="wa-qr-disconnect" disabled={busy} onClick={disconnect} className="inline-flex w-full items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium transition-colors hover:bg-[var(--input)]" style={{ minHeight: 42, borderColor: "var(--border)", color: "var(--error-fg)" }}>
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <Unplug size={16} />}{t("uwa.qr.disconnect")}
                    </button>
                </div>
            ) : (
                <div className="space-y-4" data-testid="wa-qr-pending">
                    {(stalled || (data.error && data.error !== "qr_timeout")) && (
                        <div className="rounded-xl p-3 text-xs" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }} data-testid="wa-qr-error">{stalled ? t("uwa.err.qr_unavailable") : t(`uwa.err.${data.error}` as any, { defaultValue: t("uwa.err.connection_lost") })}</div>
                    )}
                    <div className="grid gap-5 sm:grid-cols-[auto,1fr] items-start">
                        <div className="mx-auto">
                            <div className="relative rounded-xl bg-white p-2 border flex items-center justify-center" style={{ borderColor: "var(--border)", width: 206, height: 206 }}>
                                {data.qr_data_url
                                    ? <img src={data.qr_data_url} alt={t("uwa.qr.alt")} width={188} height={188} data-testid="wa-qr-image" className={left <= 2 ? "opacity-40 transition-opacity" : "transition-opacity"} />
                                    : <div className="flex flex-col items-center gap-2 text-xs text-slate-500" data-testid="wa-qr-connecting"><Loader2 size={22} className="animate-spin" style={{ color: WA_GREEN }} />{t("uwa.qr.connecting")}</div>}
                            </div>
                            {data.qr_data_url && <div className="mt-2 text-center text-xs tabular-nums" style={{ color: "var(--text-muted)" }} data-testid="wa-qr-countdown">{t("uwa.qr.expires_in", { s: left })}</div>}
                            {data.pairing_code && <div className="text-center text-xs mt-1" style={{ color: "var(--text-muted)" }}>{t("uwa.qr.code")}: <span className="font-mono font-semibold" style={{ color: "var(--text)" }} data-testid="wa-qr-code">{data.pairing_code}</span></div>}
                        </div>
                        <ol className="space-y-3" data-testid="wa-qr-steps">
                            {[1, 2, 3].map((n) => (
                                <li key={n} className="flex gap-2.5 text-sm" style={{ color: "var(--text)" }}>
                                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: WA_GREEN }}>{n}</span>
                                    <span className="pt-0.5">{t(`uwa.qr.step${n}` as any)}</span>
                                </li>
                            ))}
                            <li className="flex items-center gap-2 text-xs pt-1" style={{ color: "var(--text-muted)" }}><Loader2 size={12} className="animate-spin" />{t("uwa.qr.waiting")}</li>
                        </ol>
                    </div>
                    {data.mocked && <MockScan onDone={connected} />}
                    <button type="button" data-testid="wa-qr-refresh" disabled={loading} onClick={() => { setWaitStart(null); load(); }} className="inline-flex w-full items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium transition-colors hover:bg-[var(--input)]" style={{ minHeight: 42, borderColor: "var(--border)", color: "var(--text)" }}>
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />{t("uwa.qr.refresh")}
                    </button>
                </div>
            )}
        </WaDialog>
    );
}
